import { Contract_ChangePlaceOfDeliveryName, Tooltips_DistributionRate } from 'data/endpoints';
import IRepository from 'data/IRepository';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React, { useState } from 'react';
import ContractBasicInfoCard from '../components/ContractBasicInfoCard';
import ContractEnergyDTO from '../dtos/ContractEnergyDTO';
import SupplyPointCard from '../components/SupplyPointCard';
import PaymentMethodList from 'features/payments/components/PaymentMethodList';
import SendingMethodsCard from '../components/SendingMethodsCard';
import { trackPromise } from 'react-promise-tracker';
import PlaceOfDeliveryNameDTO from '../dtos/PlaceOfDeliveryNameDTO';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import PlaceOfDeliveryNameDialog from '../components/PlaceOfDeliveryNameDialog';
import { ContractDetail_PlaceOfDeliveryNameDialog } from 'features/shared/components/loaders/promiseLoadingAreas';
import TooltipDTO from 'features/shared/dtos/TooltipDTO';
import { getQueryParams } from 'features/shared/helpers/queryParams';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import { isEmpty, isEmptyOrUndf } from 'features/shared/helpers/objects';
import ContractFullModel from '../models/ContractFullModel';
import ContractAdditionalInfoList from 'features/contracts/components/ContractAdditionalInfoList';
import ContractHeatBasicInfoCard from '../components/ContractHeatBasicInfoCard';
import { useTranslation } from 'react-i18next';
import SpotProductDTO from '../dtos/SpotProductDTO';
import ContractSpotProductCard from '../components/ContractSpotProductCard';

const numberOfShowContacts = 3;
interface Props {
  contractModel: ContractFullModel;
  contractEnergy: ContractEnergyDTO;
  setContractEnergy: React.Dispatch<React.SetStateAction<ContractEnergyDTO>>;
  spotProduct: SpotProductDTO;
}

const ContractDetailSection = ({ contractModel, contractEnergy, setContractEnergy, spotProduct }: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;

  // DI
  const deliveryNameRepo = useInjection<IRepository<PlaceOfDeliveryNameDTO>>(IocTypes.Repository);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  // contract model destruct
  const { contract, paymentMethod, prices } = contractModel;

  // state
  const [placeOfDeliveryDialogShow, setPlaceOfDeliveryDialogShow] = useState(false);

  // handlers
  const updatePlaceOfDeliveryNameHandler = async (name: string | null) => {
    let response = await trackPromise(
      deliveryNameRepo.post({
        url: Contract_ChangePlaceOfDeliveryName,
        data: { placeOfDeliveryName: name },
        routeParams: getRouteParams("contractId", contract.contractId!),
      }), 
      ContractDetail_PlaceOfDeliveryNameDialog
    );
    
    if (response.status < 300) {
      let contractEnergyCopy: ContractEnergyDTO = {...contractEnergy};
      contractEnergyCopy.namedByCustomer = name || undefined;
      setContractEnergy(contractEnergyCopy);
    }

    setPlaceOfDeliveryDialogShow(false);
  }

  const openDistributionRateHandler = async (distributionRate: string) => {
    dialogService.openDialogAsync<TooltipDTO>({
      title: tLabels("contractDetailSection.descriptionDistributionRate"),
      request: { url: Tooltips_DistributionRate, params: getQueryParams("DistributionRate", distributionRate) },
      renderContent: (tooltip: TooltipDTO) => <div dangerouslySetInnerHTML={ {__html: tooltip.htmlText || ""} } className="m-4" />
    });
  };

  // sub-renders 
  const renderContractBasicInfo = () => {
    if (contract.contractType === "HeatSmallConsumption") {
      return (
        <ContractHeatBasicInfoCard
          contract={contract}
          contractEnergy={contractEnergy}
        />
      );
    } else {
      return (
        <ContractBasicInfoCard
          contract={contract}
          contractEnergy={contractEnergy}
          onDistributionInfoClick={openDistributionRateHandler}
        />
      );
    }
  };

  const renderSpotProduct = () => { 
    if (!isEmptyOrUndf(spotProduct)) {
      return <ContractSpotProductCard spotProduct={spotProduct} />
    }
  }

  // render
  if (!isEmpty(contract)) {
    return (
      <>
        {renderContractBasicInfo()}
        {renderSpotProduct()}
        <SupplyPointCard 
          contractEnergy={contractEnergy} 
          contractType={contract.contractType}
          setPlaceOfDeliveryDialogShow={setPlaceOfDeliveryDialogShow}
        />
        <ContractAdditionalInfoList 
          consumptions={contractEnergy.agreedConsumption!}
          prices={prices}
        />
        <SendingMethodsCard
          mailMethods={contract.mailMethod || []}
          contacts={contract.contact || []}
          address={contract.address!}
          contactsCount={numberOfShowContacts}
        />
        <PaymentMethodList paymentMethods={paymentMethod || []} />
        <PlaceOfDeliveryNameDialog
          show={placeOfDeliveryDialogShow}
          setShow={setPlaceOfDeliveryDialogShow}
          onSubmit={updatePlaceOfDeliveryNameHandler}
          placeOfDeliveryName={contractEnergy.namedByCustomer || null}
        />
      </>
    )
  }  else {
    return null;
  }
}

export default ContractDetailSection;
